/* ── ReportBuilder ────────────────────────────────────────────
   Build a formatted compliance report from saved bookmarks.
   Produces a professional A4 document via window.print() with
   cover page, TOC, sections, citations appendix, and disclaimer.
   ────────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Bookmark, Citation } from '../types';
import { loadBookmarks } from './Bookmarks';
import { fetchQueryResult } from '../api';

/* ── Types ───────────────────────────────────────────────────── */

interface ReportSection {
  bookmark: Bookmark;
  /** Full response text (fetched from backend) */
  fullResponse: string | null;
  /** Full citations (fetched from backend) */
  citations: Citation[];
  /** Loading state */
  loading: boolean;
  error: string | null;
}

interface ReportMeta {
  title: string;
  project: string;
  author: string;
  date: string;
}

interface Props {
  onClose: () => void;
}

/* ── Helpers ─────────────────────────────────────────────────── */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Escape HTML */
function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Simple markdown → HTML for the report body */
function mdToHtml(md: string): string {
  return md
    .split('\n')
    .map((line) => {
      if (line.includes('<!-- FOLLOW_UPS') || line.includes('<!-- /FOLLOW_UPS')) return '';
      const trimmed = line.trim();
      if (trimmed.startsWith('#### ')) return `<h4>${esc(trimmed.slice(5))}</h4>`;
      if (trimmed.startsWith('### ')) return `<h3>${esc(trimmed.slice(4))}</h3>`;
      if (trimmed.startsWith('## ')) return `<h2>${esc(trimmed.slice(3))}</h2>`;
      if (trimmed.startsWith('# ')) return `<h1>${esc(trimmed.slice(2))}</h1>`;
      if (trimmed.startsWith('> ')) return `<blockquote>${esc(trimmed.slice(2))}</blockquote>`;
      if (trimmed.startsWith('- ') || trimmed.startsWith('* '))
        return `<li>${esc(trimmed.slice(2))}</li>`;
      if (/^\d+\.\s/.test(trimmed))
        return `<li>${esc(trimmed.replace(/^\d+\.\s/, ''))}</li>`;
      if (trimmed === '') return '<br/>';
      return `<p>${esc(trimmed)}</p>`;
    })
    .join('\n')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\[(\d+)\]/g, '<sup class="cite-ref">[$1]</sup>');
}

/* ── Icons ───────────────────────────────────────────────────── */

function ArrowUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

/* ── Report Builder Component ────────────────────────────────── */

export default function ReportBuilder({ onClose }: Props) {
  const allBookmarks = useMemo(() => loadBookmarks(), []);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sections, setSections] = useState<ReportSection[]>([]);
  const [meta, setMeta] = useState<ReportMeta>({
    title: 'Compliance Assessment Report',
    project: '',
    author: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [view, setView] = useState<'select' | 'preview'>('select');
  const [fetching, setFetching] = useState(false);
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  /* ── Toggle bookmark selection ── */
  const toggleSelection = useCallback((queryId: string) => {
    setSelectedIds((prev) =>
      prev.includes(queryId)
        ? prev.filter((id) => id !== queryId)
        : [...prev, queryId],
    );
  }, []);

  /* ── Select all / deselect all ── */
  const selectAll = useCallback(() => {
    setSelectedIds(allBookmarks.map((b) => b.query_id));
  }, [allBookmarks]);

  const deselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  /* ── Move section up/down ── */
  const moveSection = useCallback((idx: number, direction: 'up' | 'down') => {
    setSections((prev) => {
      const arr = [...prev];
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= arr.length) return prev;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  }, []);

  /* ── Remove section ── */
  const removeSection = useCallback((idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ── Fetch full data for selected bookmarks and enter preview ── */
  const buildReport = useCallback(async () => {
    const selected = allBookmarks.filter((b) => selectedIds.includes(b.query_id));
    if (selected.length === 0) return;

    setFetching(true);

    const newSections: ReportSection[] = selected.map((b) => ({
      bookmark: b,
      fullResponse: null,
      citations: [],
      loading: true,
      error: null,
    }));

    setSections(newSections);
    setView('preview');

    // Fetch full responses in parallel
    const results = await Promise.allSettled(
      selected.map(async (b, i) => {
        try {
          const result = await fetchQueryResult(b.query_id);
          if (result.status === 'complete' && result.response_text) {
            const citations: Citation[] = (result.citations ?? []).map((c, ci) => ({
              id: c.id,
              number: ci + 1,
              standard: c.standard,
              clause: c.clause_number ?? '',
              text: '',
            }));
            return { index: i, response: result.response_text, citations };
          }
          return { index: i, response: b.response_preview, citations: [] };
        } catch {
          return { index: i, response: null, citations: [], error: 'Failed to fetch' };
        }
      }),
    );

    setSections((prev) =>
      prev.map((section, i) => {
        const r = results[i];
        if (r.status === 'fulfilled') {
          return {
            ...section,
            fullResponse: r.value.response ?? section.bookmark.response_preview,
            citations: r.value.citations,
            loading: false,
            error: ('error' in r.value) ? (r.value as { error: string }).error : null,
          };
        }
        return {
          ...section,
          fullResponse: section.bookmark.response_preview,
          loading: false,
          error: 'Failed to load full response',
        };
      }),
    );

    setFetching(false);
  }, [allBookmarks, selectedIds]);

  /* ── Collect all unique citations for appendix ── */
  const allCitations = useMemo(() => {
    const seen = new Set<string>();
    const result: { standard: string; clause: string; text: string }[] = [];
    for (const section of sections) {
      for (const c of section.citations) {
        const key = `${c.standard}::${c.clause}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({ standard: c.standard, clause: c.clause, text: c.text });
        }
      }
      // Also collect from bookmark citation strings
      for (const ref of section.bookmark.citations) {
        if (!seen.has(ref)) {
          seen.add(ref);
          result.push({ standard: ref, clause: '', text: '' });
        }
      }
    }
    return result;
  }, [sections]);

  /* ── Generate printable HTML ── */
  const generatePrintHtml = useCallback((): string => {
    const dateFormatted = formatDate(meta.date);

    // Cover page
    const coverHtml = `
      <div class="report-cover">
        <div class="cover-watermark">COMPLIANCE HUB</div>
        <div class="cover-content">
          <h1 class="cover-title">${esc(meta.title)}</h1>
          ${meta.project ? `<div class="cover-project">${esc(meta.project)}</div>` : ''}
          <div class="cover-meta">
            ${meta.author ? `<div class="cover-author">Prepared by: ${esc(meta.author)}</div>` : ''}
            <div class="cover-date">${esc(dateFormatted)}</div>
          </div>
          <div class="cover-sections">${sections.length} section${sections.length !== 1 ? 's' : ''} · ${allCitations.length} citation${allCitations.length !== 1 ? 's' : ''}</div>
        </div>
      </div>
    `;

    // Table of contents
    const tocHtml = `
      <div class="report-toc">
        <h2 class="toc-heading">Table of Contents</h2>
        <ol class="toc-list">
          ${sections.map((s, i) => `
            <li class="toc-item">
              <span class="toc-num">${i + 1}.</span>
              <span class="toc-text">${esc(s.bookmark.query)}</span>
            </li>
          `).join('')}
          ${allCitations.length > 0 ? `
            <li class="toc-item toc-item--appendix">
              <span class="toc-num">A.</span>
              <span class="toc-text">Citations Appendix</span>
            </li>
          ` : ''}
        </ol>
      </div>
    `;

    // Sections
    const sectionsHtml = sections.map((s, i) => {
      const cleanContent = (s.fullResponse ?? s.bookmark.response_preview)
        .replace(/<!--\s*FOLLOW_UPS\s*-->[\s\S]*?<!--\s*\/FOLLOW_UPS\s*-->/, '')
        .trim();

      const citationsHtml = s.citations.length > 0
        ? `<div class="section-citations">
            <div class="section-citations-header">Referenced Standards</div>
            <ul class="section-citations-list">
              ${s.citations.map((c) =>
                `<li><span class="citation-standard">${esc(c.standard)}</span>${c.clause ? ` — §${esc(c.clause)}` : ''}</li>`
              ).join('')}
            </ul>
          </div>`
        : '';

      return `
        <div class="report-section">
          <div class="section-number">Section ${i + 1}</div>
          <h2 class="section-query">${esc(s.bookmark.query)}</h2>
          <div class="section-body">${mdToHtml(cleanContent)}</div>
          ${citationsHtml}
        </div>
      `;
    }).join('');

    // Citations appendix
    const appendixHtml = allCitations.length > 0 ? `
      <div class="report-appendix">
        <h2 class="appendix-heading">Appendix A — Citations</h2>
        <table class="appendix-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Standard</th>
              <th>Clause</th>
            </tr>
          </thead>
          <tbody>
            ${allCitations.map((c, i) => `
              <tr>
                <td class="appendix-num">${i + 1}</td>
                <td class="appendix-standard">${esc(c.standard)}</td>
                <td class="appendix-clause">${esc(c.clause || '—')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${esc(meta.title)}</title>
  <style>
    /* ── Report Print Stylesheet ───────────────────────────────── */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @page {
      size: A4;
      margin: 25mm 20mm 30mm 20mm;
      @bottom-center {
        content: counter(page);
        font-family: 'Inter', sans-serif;
        font-size: 9pt;
        color: #71717a;
      }
    }

    body {
      font-family: 'Inter', -apple-system, system-ui, sans-serif;
      font-size: 10.5pt;
      line-height: 1.6;
      color: #1a1a1f;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ── Cover Page ── */
    .report-cover {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      text-align: center;
      page-break-after: always;
      break-after: page;
      position: relative;
    }

    .cover-watermark {
      position: absolute;
      top: 60px;
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      font-size: 11pt;
      letter-spacing: 0.3em;
      color: #c4c4cb;
      text-transform: uppercase;
    }

    .cover-content {
      max-width: 480px;
    }

    .cover-title {
      font-family: 'Inter', sans-serif;
      font-size: 28pt;
      font-weight: 700;
      color: #1a1a1f;
      line-height: 1.2;
      margin-bottom: 16px;
    }

    .cover-project {
      font-size: 14pt;
      font-weight: 500;
      color: #52525b;
      margin-bottom: 32px;
    }

    .cover-meta {
      border-top: 2px solid #6366f1;
      padding-top: 16px;
      margin-bottom: 12px;
    }

    .cover-author {
      font-size: 11pt;
      color: #3f3f46;
      margin-bottom: 4px;
    }

    .cover-date {
      font-size: 11pt;
      color: #71717a;
    }

    .cover-sections {
      margin-top: 24px;
      font-size: 9.5pt;
      color: #a1a1ab;
    }

    /* ── Table of Contents ── */
    .report-toc {
      page-break-after: always;
      break-after: page;
      padding-top: 20px;
    }

    .toc-heading {
      font-family: 'Inter', sans-serif;
      font-size: 18pt;
      font-weight: 600;
      color: #1a1a1f;
      margin-bottom: 24px;
      padding-bottom: 8px;
      border-bottom: 2px solid #6366f1;
    }

    .toc-list {
      list-style: none;
      padding: 0;
    }

    .toc-item {
      display: flex;
      gap: 10px;
      padding: 8px 0;
      border-bottom: 1px solid #e0e0e5;
      font-size: 11pt;
    }

    .toc-item--appendix {
      margin-top: 16px;
      border-top: 1px solid #c4c4cb;
      padding-top: 16px;
      font-style: italic;
    }

    .toc-num {
      font-weight: 600;
      color: #6366f1;
      min-width: 28px;
    }

    .toc-text {
      color: #1a1a1f;
    }

    /* ── Sections ── */
    .report-section {
      page-break-before: always;
      break-before: page;
      padding-top: 20px;
    }

    .section-number {
      font-family: 'Inter', sans-serif;
      font-size: 9pt;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.15em;
      color: #6366f1;
      margin-bottom: 6px;
    }

    .section-query {
      font-family: 'Inter', sans-serif;
      font-size: 16pt;
      font-weight: 600;
      color: #1a1a1f;
      line-height: 1.3;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e0e0e5;
    }

    .section-body p { margin: 6px 0; }
    .section-body h1, .section-body h2, .section-body h3, .section-body h4 {
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      margin-top: 16px;
      margin-bottom: 6px;
    }
    .section-body h1 { font-size: 14pt; }
    .section-body h2 { font-size: 13pt; }
    .section-body h3 { font-size: 12pt; }
    .section-body h4 { font-size: 11pt; }

    .section-body blockquote {
      border-left: 3px solid #6366f1;
      margin: 10px 0;
      padding: 6px 14px;
      background: #fafafb;
      color: #3f3f46;
    }

    .section-body code {
      font-family: 'JetBrains Mono', monospace;
      font-size: 9.5pt;
      background: #f3f3f5;
      padding: 1px 4px;
      border-radius: 3px;
    }

    .section-body li {
      margin-left: 20px;
      margin-bottom: 3px;
    }

    .section-body strong { font-weight: 600; }

    .cite-ref {
      color: #6366f1;
      font-size: 8pt;
      font-weight: 600;
    }

    .section-citations {
      margin-top: 20px;
      padding: 12px 16px;
      background: #fafafb;
      border: 1px solid #e0e0e5;
      border-radius: 4px;
    }

    .section-citations-header {
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      font-size: 9pt;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #71717a;
      margin-bottom: 8px;
    }

    .section-citations-list {
      list-style: none;
      padding: 0;
    }

    .section-citations-list li {
      font-family: 'JetBrains Mono', monospace;
      font-size: 9pt;
      color: #3f3f46;
      padding: 3px 0;
      border-bottom: 1px solid #e9e9ed;
    }

    .section-citations-list li:last-child { border-bottom: none; }

    .citation-standard {
      font-weight: 500;
    }

    /* ── Citations Appendix ── */
    .report-appendix {
      page-break-before: always;
      break-before: page;
      padding-top: 20px;
    }

    .appendix-heading {
      font-family: 'Inter', sans-serif;
      font-size: 18pt;
      font-weight: 600;
      color: #1a1a1f;
      margin-bottom: 20px;
      padding-bottom: 8px;
      border-bottom: 2px solid #6366f1;
    }

    .appendix-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9.5pt;
    }

    .appendix-table th {
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      font-size: 8.5pt;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #71717a;
      text-align: left;
      padding: 8px 10px;
      border-bottom: 2px solid #e0e0e5;
    }

    .appendix-table td {
      padding: 6px 10px;
      border-bottom: 1px solid #e9e9ed;
      vertical-align: top;
    }

    .appendix-num {
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
      color: #6366f1;
      width: 36px;
    }

    .appendix-standard {
      font-family: 'JetBrains Mono', monospace;
      font-weight: 500;
    }

    .appendix-clause {
      font-family: 'JetBrains Mono', monospace;
      color: #52525b;
    }

    /* ── Disclaimer Footer ── */
    .report-disclaimer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 7.5pt;
      color: #a1a1ab;
      padding: 6px 20mm;
      border-top: 0.5px solid #e0e0e5;
      font-family: 'Inter', sans-serif;
    }

    /* ── Print-specific ── */
    @media print {
      body { margin: 0; padding: 0; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  ${coverHtml}
  ${tocHtml}
  ${sectionsHtml}
  ${appendixHtml}
  <div class="report-disclaimer">
    This report is a research aid generated by Compliance Hub. It does not constitute professional advice.
    Always verify against official published standards and consult qualified professionals for compliance decisions.
  </div>
</body>
</html>`;
  }, [meta, sections, allCitations]);

  /* ── Print / Export ── */
  const handleExport = useCallback(async () => {
    const html = generatePrintHtml();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    // A4 (2026-06-20): native share sheet removed — export simplified to
    // PDF (print) / HTML download only. No social/native-share destinations.
    const w = window.open(url, '_blank');
    if (w) {
      w.onload = () => {
        setTimeout(() => {
          w.print();
          URL.revokeObjectURL(url);
        }, 600);
      };
    } else {
      // Popup blocked — download instead
      const a = document.createElement('a');
      a.href = url;
      a.download = `${meta.title.replace(/\s+/g, '-').toLowerCase()}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [generatePrintHtml, meta.title]);

  /* ── Keyboard: Escape to close ── */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  /* ── Render: Selection View ── */
  if (view === 'select') {
    return (
      <div className="rb-overlay" onClick={onClose}>
        <div className="rb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Report Builder">
          <div className="rb-header">
            <div className="rb-header-left">
              <ReportIcon />
              <h2 className="rb-title">Report Builder</h2>
            </div>
            <button className="rb-close" onClick={onClose} aria-label="Close">✕</button>
          </div>

          {/* Metadata form */}
          <div className="rb-meta-form">
            <div className="rb-field">
              <label className="rb-label" htmlFor="rb-title">Report Title</label>
              <input
                id="rb-title"
                className="rb-input"
                type="text"
                value={meta.title}
                onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
                placeholder="Compliance Assessment Report"
              />
            </div>
            <div className="rb-field-row">
              <div className="rb-field">
                <label className="rb-label" htmlFor="rb-project">Project Name</label>
                <input
                  id="rb-project"
                  className="rb-input"
                  type="text"
                  value={meta.project}
                  onChange={(e) => setMeta((m) => ({ ...m, project: e.target.value }))}
                  placeholder="e.g. 123 Smith Street"
                />
              </div>
              <div className="rb-field">
                <label className="rb-label" htmlFor="rb-author">Author</label>
                <input
                  id="rb-author"
                  className="rb-input"
                  type="text"
                  value={meta.author}
                  onChange={(e) => setMeta((m) => ({ ...m, author: e.target.value }))}
                  placeholder="Your name"
                />
              </div>
              <div className="rb-field rb-field--date">
                <label className="rb-label" htmlFor="rb-date">Date</label>
                <input
                  id="rb-date"
                  className="rb-input"
                  type="date"
                  value={meta.date}
                  onChange={(e) => setMeta((m) => ({ ...m, date: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Bookmark selection */}
          <div className="rb-section-header">
            <span className="rb-section-label">
              Select Bookmarks ({selectedIds.length} of {allBookmarks.length})
            </span>
            <div className="rb-section-actions">
              <button className="rb-text-btn" onClick={selectAll}>Select all</button>
              <button className="rb-text-btn" onClick={deselectAll}>Clear</button>
            </div>
          </div>

          <div className="rb-bookmark-list">
            {allBookmarks.length === 0 && (
              <div className="rb-empty">
                <p>No reports yet.</p>
                <p className="rb-empty-hint">Start one from the chat — select content from an answer and click ‘Add to Report’, or bookmark a query and it will appear here.</p>
              </div>
            )}
            {allBookmarks.map((b) => (
              <label key={b.query_id} className="rb-bookmark-item">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(b.query_id)}
                  onChange={() => toggleSelection(b.query_id)}
                  className="rb-checkbox"
                />
                <div className="rb-bookmark-content">
                  <div className="rb-bookmark-query">{b.query}</div>
                  <div className="rb-bookmark-meta">
                    {b.citations.length > 0 && (
                      <span className="rb-bookmark-citations">
                        {b.citations.slice(0, 2).join(' · ')}
                        {b.citations.length > 2 && ` +${b.citations.length - 2}`}
                      </span>
                    )}
                    <span className="rb-bookmark-date">{formatDate(b.created_at)}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Footer */}
          <div className="rb-footer">
            <button className="rb-btn rb-btn--secondary" onClick={onClose}>Cancel</button>
            <button
              className="rb-btn rb-btn--primary"
              onClick={buildReport}
              disabled={selectedIds.length === 0}
            >
              Build Report ({selectedIds.length})
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render: Preview View ── */
  return (
    <div className="rb-overlay" onClick={onClose}>
      <div className="rb-modal rb-modal--preview" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Report Preview">
        <div className="rb-header">
          <div className="rb-header-left">
            <ReportIcon />
            <h2 className="rb-title">Report Preview</h2>
          </div>
          <div className="rb-header-actions">
            <button className="rb-btn rb-btn--secondary rb-btn--sm" onClick={() => setView('select')}>
              ← Edit
            </button>
            <button
              className="rb-btn rb-btn--primary rb-btn--sm"
              onClick={handleExport}
              disabled={fetching}
            >
              {fetching ? 'Loading…' : '🖨 Export PDF'}
            </button>
            <button className="rb-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {/* Section reorder controls */}
        <div className="rb-reorder-bar">
          <span className="rb-reorder-label">Sections — drag to reorder</span>
          <div className="rb-reorder-chips">
            {sections.map((s, i) => (
              <div key={s.bookmark.query_id} className="rb-reorder-chip">
                <span className="rb-reorder-chip-num">{i + 1}</span>
                <span className="rb-reorder-chip-text">{s.bookmark.query.slice(0, 40)}{s.bookmark.query.length > 40 ? '…' : ''}</span>
                <div className="rb-reorder-chip-actions">
                  <button
                    className="rb-icon-btn"
                    onClick={() => moveSection(i, 'up')}
                    disabled={i === 0}
                    aria-label="Move up"
                    title="Move up"
                  >
                    <ArrowUpIcon />
                  </button>
                  <button
                    className="rb-icon-btn"
                    onClick={() => moveSection(i, 'down')}
                    disabled={i === sections.length - 1}
                    aria-label="Move down"
                    title="Move down"
                  >
                    <ArrowDownIcon />
                  </button>
                  <button
                    className="rb-icon-btn rb-icon-btn--danger"
                    onClick={() => removeSection(i)}
                    aria-label="Remove section"
                    title="Remove"
                  >
                    <RemoveIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview content */}
        <div className="rb-preview-scroll">
          {/* Cover preview */}
          <div className="rb-preview-cover">
            <div className="rb-preview-watermark">COMPLIANCE HUB</div>
            <h1 className="rb-preview-title">{meta.title}</h1>
            {meta.project && <div className="rb-preview-project">{meta.project}</div>}
            <div className="rb-preview-meta-line" />
            {meta.author && <div className="rb-preview-author">Prepared by: {meta.author}</div>}
            <div className="rb-preview-date">{formatDate(meta.date)}</div>
          </div>

          {/* TOC preview */}
          <div className="rb-preview-toc">
            <h3 className="rb-preview-toc-heading">Table of Contents</h3>
            <ol className="rb-preview-toc-list">
              {sections.map((s, i) => (
                <li key={s.bookmark.query_id} className="rb-preview-toc-item">
                  <span className="rb-preview-toc-num">{i + 1}.</span>
                  {s.bookmark.query}
                </li>
              ))}
              {allCitations.length > 0 && (
                <li className="rb-preview-toc-item rb-preview-toc-item--appendix">
                  <span className="rb-preview-toc-num">A.</span>
                  Citations Appendix
                </li>
              )}
            </ol>
          </div>

          {/* Section previews */}
          {sections.map((s, i) => (
            <div key={s.bookmark.query_id} className="rb-preview-section">
              <div className="rb-preview-section-num">Section {i + 1}</div>
              <h3 className="rb-preview-section-query">{s.bookmark.query}</h3>
              {s.loading ? (
                <div className="rb-loading">Loading full response…</div>
              ) : s.error ? (
                <div className="rb-error">{s.error} — using preview text</div>
              ) : null}
              <div className="rb-preview-section-body">
                {(s.fullResponse ?? s.bookmark.response_preview).slice(0, 600)}
                {(s.fullResponse ?? s.bookmark.response_preview).length > 600 ? '…' : ''}
              </div>
              {s.citations.length > 0 && (
                <div className="rb-preview-section-citations">
                  {s.citations.map((c) => (
                    <span key={c.id} className="rb-citation-chip">{c.standard}{c.clause ? ` §${c.clause}` : ''}</span>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Appendix preview */}
          {allCitations.length > 0 && (
            <div className="rb-preview-appendix">
              <h3 className="rb-preview-appendix-heading">Appendix A — Citations ({allCitations.length})</h3>
              <div className="rb-preview-appendix-list">
                {allCitations.slice(0, 20).map((c, i) => (
                  <div key={`${c.standard}-${c.clause}-${i}`} className="rb-appendix-row">
                    <span className="rb-appendix-num">{i + 1}</span>
                    <span className="rb-appendix-standard">{c.standard}</span>
                    <span className="rb-appendix-clause">{c.clause || '—'}</span>
                  </div>
                ))}
                {allCitations.length > 20 && (
                  <div className="rb-appendix-more">+{allCitations.length - 20} more in full report</div>
                )}
              </div>
            </div>
          )}

          {/* Disclaimer preview */}
          <div className="rb-preview-disclaimer">
            This report is a research aid generated by Compliance Hub. It does not constitute professional advice.
            Always verify against official published standards and consult qualified professionals for compliance decisions.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Named export for button use in other components ──── */
export { ReportIcon };
